import { FC, ReactNode, useRef, useState } from 'react';
import classNames from 'classnames';

import { Portal } from '../Portal/Portal';
import { Typography } from '../Typography/Typography';
import styles from './Tooltip.module.css';

interface TooltipProps {
	message: ReactNode;
	children: ReactNode;
	position?: ['top' | 'bottom' | 'middle', 'left' | 'right' | 'center' | 'left-side'];
	className?: string;
	containerClassName?: string;
	messageWidth?: string;
	color?: 'secondary' | 'primary';
	size?: 'small' | 'default';
}

export const Tooltip: FC<TooltipProps> = ({
	message,
	children,
	position = [`bottom`, `center`],
	className,
	containerClassName,
	messageWidth,
	color = `secondary`,
	size = `default`,
}) => {
	const { Text } = Typography;

	const tooltipChildrenRef = useRef<HTMLDivElement | null>(null);
	const [showTooltip, setShowTooltip] = useState(false);
	const [coords, setCoords] = useState<
		| {
				left: number;
				top: number;
		  }
		| undefined
	>();

	return (
		<div className={classNames(styles.tooltip__container, containerClassName)}>
			<div
				className={classNames(
					styles.tooltip__btn,
					!children && styles[`tooltip__btn--without-child`],
					`truncate`,
					className,
				)}
				ref={tooltipChildrenRef}
				onMouseEnter={e => {
					const rect = e.currentTarget.getBoundingClientRect();
					setCoords({
						left: rect.x + rect.width / 2,
						top: position[0] === `top` ? rect.y + window.scrollY : rect.y + window.scrollY + rect.height,
					});
					setShowTooltip(true);
				}}
				onMouseLeave={() => {
					setCoords(undefined);
					setShowTooltip(false);
				}}
			>
				{children}
			</div>
			{message && (
				<Portal>
					<div
						className={classNames(
							styles[`tooltip__content-wrapper`],
							styles[`tooltip__content-wrapper--${size}`],
							styles[`tooltip__content-wrapper--${position[0]}`],
							styles[`tooltip__content-wrapper--${position[1]}`],
							showTooltip && styles[`tooltip__content-wrapper--show`],
						)}
						style={{
							left: coords?.left,
							top: coords?.top,
						}}
					>
						<Text
							variant='note'
							className={styles[`tooltip__message--${color}`]}
							style={{ maxWidth: messageWidth, wordBreak: `break-word` }}
						>
							{message}
						</Text>
					</div>
				</Portal>
			)}
		</div>
	);
};
